import React, { createContext, useContext, useEffect, useState } from 'react';
import { de, enGB } from 'date-fns/locale';

const LANG_KEY = 'life_manager_lang';

// The chosen language, readable outside React (widget bridge, helpers that run
// before/without the provider). localStorage is the single source of truth —
// the provider seeds itself from it and writes back on every change.
export const getLang = () => {
  try {
    const stored = window.localStorage.getItem(LANG_KEY);
    if (stored === 'en' || stored === 'de') return stored;
  } catch (_) {}
  return 'de';
};

// date-fns locale for the current language. Every `format(..., { locale })`
// call in the app goes through this so month and weekday names follow the
// language setting instead of always being German. enGB keeps Monday as the
// first day of the week, which is what the calendar grid assumes.
export const localeFor = (lang) => (lang === 'en' ? enGB : de);
export const getDateLocale = () => localeFor(getLang());

// Mo…So / Mon…Sun for the calendar grid header, Monday first. Derived from the
// locale so the app calendar and the home-screen widget always agree.
// `short` is the two-letter form: Mo Di Mi Do Fr Sa So / Mo Tu We Th Fr Sa Su.
export const weekdayLabels = (locale = getDateLocale()) =>
  Array.from({ length: 7 }, (_, i) =>
    locale.localize.day((i + 1) % 7, { width: 'short' })
  );

// Translation dictionary. Keys are dot-paths; values may include {placeholder}s.
// Add new keys as components are translated.
const dict = {
  en: {
    // App / shell ---------------------------------------------------------
    'app.title': 'Life Manager',
    'app.tagline': 'Track, Budget & Plan',
    'app.loading': 'Loading…',
    'app.settings': 'Settings',
    'app.signOut': 'Sign out',
    'app.syncError': 'Sync error: {message}',

    // Common --------------------------------------------------------------
    'common.save': 'Save',
    'common.cancel': 'Cancel',
    'common.delete': 'Delete',
    'common.edit': 'Edit',
    'common.add': 'Add',
    'common.back': 'Back',
    'common.confirm': 'Confirm',
    'common.close': 'Close',
    'common.yes': 'Yes',
    'common.no': 'No',
    'common.loading': 'Loading…',
    'common.uploading': 'Uploading…',
    'common.search': 'Search',
    'common.today': 'Today',
    'common.yesterday': 'Yesterday',
    'widget.openApp': 'Open Eulea to load the calendar.',
    'common.tomorrow': 'Tomorrow',
    'common.upcoming': 'Upcoming',
    'common.all': 'All',
    'common.completed': 'Completed',
    'common.untitled': 'Untitled',
    'common.optional': 'optional',
    'common.note': 'Note',
    'common.priority': 'Priority',
    'common.date': 'Date',
    'common.title': 'Title',

    // Auth ----------------------------------------------------------------
    'auth.signInTitle': 'Sign in to your account',
    'auth.signUpTitle': 'Create a new account',
    'auth.email': 'Email',
    'auth.password': 'Password',
    'auth.signIn': 'Sign in',
    'auth.signUp': 'Sign up',
    'auth.noAccount': 'No account?',
    'auth.haveAccount': 'Already have an account?',
    'auth.pleaseWait': 'Please wait…',
    'auth.failed': 'Authentication failed',
    'auth.confirmEmail': 'Account created. Check your email to confirm, then sign in.',
    'onboarding.nameTitle': 'Welcome!',
    'onboarding.nameHint': 'Choose a display name. It will be shown to members of shared calendars.',
    'onboarding.continue': 'Continue',

    // Settings ------------------------------------------------------------
    'settings.title': 'Settings',
    'settings.menuOrder': 'Menu order',
    'settings.menuOrderHint': 'Move main groups up or down with the arrows.',
    'settings.enabledFeatures': 'Enabled features',
    'settings.enabledFeaturesHint': 'Enable or disable features individually. Click a feature for more info.',
    'settings.moveUp': 'Move up',
    'settings.moveDown': 'Move down',
    'settings.localFound': 'Local data found',
    'settings.localFoundHint':
      'You have data from the local version of this app in your browser. Click the button to import it into the cloud.',
    'settings.importLocal': 'Import local data into cloud',
    'settings.importing': 'Importing…',
    'settings.importSuccess': '✅ Import successful!',
    'settings.importFailed': '❌ Import failed.',
    'settings.confirmMigrate':
      'Import data from local browser storage into the cloud? Existing cloud data of this account will be merged with local data (matching IDs will be overwritten).',
    'settings.backup': 'Backup',
    'settings.backupHint': 'Export all your data as a JSON file or import a previous backup.',
    'settings.exportJson': 'Export JSON',
    'settings.importJson': 'Import JSON',
    'settings.language': 'Language',
    'settings.languageHint': 'Choose the language for the entire app.',
    'settings.languageEnglish': 'English',
    'settings.languageGerman': 'German',
    'settings.appearance': 'Appearance',
    'settings.appearanceHint': 'Choose your theme mode and a personal accent color.',
    'settings.themeMode': 'Theme',
    'settings.themeLight': 'Light',
    'settings.themeDark': 'Dark',
    'settings.themeSystem': 'System',
    'settings.primaryColor': 'Accent color',
    'settings.showPersonalCalendar': 'Show personal calendar',
    'settings.showPersonalCalendarHint': 'Hide the personal calendar from the sidebar (data is kept).',
    'settings.sharedCalendarsVisibility': 'Shared calendars',
    'settings.profile': 'Profile',
    'settings.profileHint': 'Your name and avatar are shown to members of shared calendars.',
    'settings.profileName': 'Display name',
    'settings.profileNamePlaceholder': 'e.g. Jana',
    'settings.profileNoName': 'No name set',
    'settings.profileColor': 'Avatar color',
    'settings.profileEmoji': 'Avatar',
    'settings.profileEmojiNone': 'Use first letter',
    'settings.profileSaved': 'Saved',
    'settings.profileVisibility': 'Visible to calendar members',
    'settings.profileKindEmoji': 'Avatar',
    'settings.profileKindPhoto': 'Photo',
    'settings.profilePhotoUpload': 'Upload photo',
    'settings.profilePhotoReplace': 'Replace photo',
    'settings.profilePhotoUploading': 'Uploading…',
    'settings.profilePhotoRemove': 'Remove photo',
    'settings.profilePhotoHint': 'JPG or PNG, max 5 MB. Public to calendar members.',
    'settings.profilePhotoTooLarge': 'Image is too large (max 5 MB).',
    'sharedCalendar.createTitle': 'Create shared calendar',
    'sharedCalendar.namePlaceholder': 'Calendar name (e.g. Family)',
    'sharedCalendar.create': 'Create',
    'sharedCalendar.createButton': 'Create shared calendar',
    'sharedCalendar.shareTitle': 'Share this calendar',
    'sharedCalendar.shareHint': 'Anyone signed in who opens this link is added as a member.',
    'sharedCalendar.copyLink': 'Copy link',
    'sharedCalendar.copied': 'Copied!',
    'sharedCalendar.delete': 'Delete calendar',
    'sharedCalendar.deleteConfirm': 'Delete this shared calendar for everyone? This cannot be undone.',
    'sharedCalendar.leave': 'Leave calendar',
    'sharedCalendar.leaveConfirm': 'Leave this shared calendar? You will lose access to its events.',
    'sharedCalendar.joinSuccess': 'Joined shared calendar',
    'sharedCalendar.joinFailed': 'Could not join calendar: {message}',
    'sharedCalendar.owner': 'Owner',
    'sharedCalendar.member': 'Member',
    'sharedCalendar.settings': 'Calendar settings',
    'sharedCalendar.membersTitle': 'Members',
    'sharedCalendar.invite': 'Invite link',
    'sharedCalendar.loadingMembers': 'Loading members…',
    'sharedCalendar.you': 'You',
    'sharedCalendar.createdBy': 'Created by',
    'sharedCalendar.unknownUser': 'Unknown user',
    'sharedCalendar.participants': 'Participants',
    'sharedCalendar.addParticipant': 'Add…',
    'sharedCalendar.allAdded': 'Everyone is already a participant.',
    'sharedCalendar.withBirthdays': 'Add a birthday list',
    'sharedCalendar.withBirthdaysHint': 'A separate birthday list just for this calendar. All members can add and edit entries.',
    'sharedCalendar.birthdays': 'Birthdays',
    'sharedCalendar.manageBirthdays': 'Manage birthdays',
    'sharedCalendar.birthdaysSection': 'Birthday list',
    'sharedCalendar.enableBirthdays': 'Enable birthday list',
    'sharedCalendar.enableBirthdaysHint': 'Shared with every member of this calendar.',
    'sharedCalendar.backToCalendar': 'Back to calendar',
    'color.blue': 'Blue',
    'color.lightBlue': 'Light blue',
    'color.green': 'Green',
    'color.pink': 'Pink',
    'color.lightPink': 'Light pink',
    'color.yellow': 'Yellow',
    'color.orange': 'Orange',
    'color.black': 'Black',
    'color.white': 'White',

    // Sidebar / feature groups --------------------------------------------
    'nav.more': 'More',
    'nav.allPages': 'All pages',
    'settings.account': 'Your account',
    'settings.deleteAccount': 'Delete account',
    'settings.deletingAccount': 'Deleting…',
    'settings.deleteAccountHint':
      'Deleting removes your login and everything in it — calendars, budget, diary, photos. This cannot be undone.',
    'settings.deleteAccountConfirm':
      'Permanently delete your account and all of its data? This cannot be undone.',
    'settings.deleteAccountTypeTitle': 'Type DELETE to confirm',
    'settings.deleteAccountWord': 'DELETE',
    'settings.deleteAccountFailed': 'Could not delete the account: {message}',
    'settings.tabBar': 'Tab bar',
    'settings.tabBarHint': 'Pick up to {n} pages for the bar at the bottom of the screen. Everything else stays under "More".',
    'nav.dashboard': 'Dashboard',
    'nav.planning': 'Planning',
    'nav.calendar': 'Calendar',
    'nav.birthdays': 'Birthday Calendar',
    'nav.todo': 'Todo',
    'nav.reminders': 'Reminders',
    'nav.finance': 'Finance',
    'nav.budget': 'Budget',
    'nav.savings': 'Savings',
    'nav.fitness': 'Fitness',
    'nav.fitnessOverview': 'Overview',
    'nav.sports': 'Sports',
    'nav.personal': 'Personal',
    'nav.diary': 'Diary',
    'nav.habits': 'Habit Tracker',
    'nav.work': 'Work',
    'nav.timetracker': 'Time Tracker',
    'nav.shopping': 'Shopping List',
    'nav.fuel': 'Fuel Log',
    'nav.tutoring': 'Tutoring',
    'nav.projects': 'Projects',

    'navDesc.dashboard':
      'Weekly overview with all upcoming appointments, birthdays and a quick look at your most important data.',
    'navDesc.planning': 'All about planning: calendar, birthdays, tasks and reminders.',
    'navDesc.calendar': 'Monthly view with events, colored labels and recurrences.',
    'navDesc.birthdays': 'Manage birthdays separately and sync them with the main calendar.',
    'navDesc.todo': 'Simple to-do list for your tasks.',
    'navDesc.reminders': 'Reminders for important things.',
    'navDesc.finance': 'All about money: budget, savings and accounts.',
    'navDesc.budget': "This month's income and expenses and trends.",
    'navDesc.savings': 'Track savings goals, accounts and saving rates.',
    'navDesc.fitness':
      'Track workouts, personal bests and plan recurring training via the calendar.',
    'navDesc.fitnessOverview': 'Active sports and statistics.',
    'navDesc.sports':
      'Detail views per sport including workouts, PRs and workout structures.',
    'navDesc.personal': 'Personal: diary, habits and more.',
    'navDesc.diary': 'Personal diary.',
    'navDesc.habits': 'Track habits and build streaks.',
    'navDesc.work': 'Tools for work: time tracking and more.',
    'navDesc.timetracker': 'Track working time.',
    'navDesc.shopping': 'Shopping lists with recipes and quantity calculation.',
    'navDesc.fuel': 'Shared fuel log: track odometer readings and split fill-up costs by km driven.',
    'navDesc.tutoring': 'Track tutoring sessions per student and semester. Mark sessions as paid when settled.',
    'navDesc.projects': 'Per-project P&L ledger: log every expense and income, see cumulative balance and break-even per project.',

    // Diary ---------------------------------------------------------------
    'diary.title': 'Diary',
    'diary.subtitle': 'Personal diary',
    'diary.newEntry': 'New entry',
    'diary.firstEntry': 'Create your first entry',
    'diary.empty': 'No entries yet. Start your diary!',
    'diary.titlePlaceholder': 'Title…',
    'diary.bodyPlaceholder': 'Write your entry here…',
    'diary.addPhoto': 'Add photo',
    'diary.removePhoto': 'Remove photo',
    'diary.deleteConfirm': 'Delete entry?',
    'diary.morePhotos': '+{count} more photos',
    'diary.uploadFailed': 'Photo upload failed: {message}',
    'diary.showTitleInList': 'Show title in list',
    'diary.showTitleInListHint': 'If off, the list shows only the date for this entry.',

    // Todo ----------------------------------------------------------------
    'todo.title': 'Todo',
    'todo.subtitle': 'Manage tasks',
    'todo.newTaskPlaceholder': 'What needs to be done?',
    'todo.notePlaceholder': 'Additional info…',
    'todo.allGroups': 'All groups',
    'todo.newGroup': 'New group',
    'todo.newGroupShort': 'New',
    'todo.manageGroups': 'Manage groups',
    'todo.groupNamePrompt': 'Name of the new group',
    'todo.priorityHigh': 'High',
    'todo.priorityMedium': 'Medium',
    'todo.priorityLow': 'Low',
    'todo.viewToday': 'Today',
    'todo.viewUpcoming': 'Upcoming',
    'todo.viewAll': 'All',
    'todo.viewCompleted': 'Completed',
    'todo.editTitle': 'Edit task',
    'todo.newTitle': 'New task',
    'todo.dateOptional': 'Date (optional)',
    'todo.priorityOptional': 'Priority (optional)',
    'todo.groupOptional': 'Group (optional)',
    'todo.noteOptional': 'Note (optional)',
    'todo.noGroup': 'None',
    'todo.markDone': 'Mark done',
    'todo.markOpen': 'Mark as open',
    'todo.overdue': 'overdue',
    'todo.emptyToday': 'No tasks for today.',
    'todo.emptyUpcoming': 'No upcoming tasks.',
    'todo.emptyAll': 'No tasks yet.',
    'todo.emptyCompleted': 'Nothing completed yet.',
    'todo.noGroups': 'No groups yet.',
    'todo.noDate': 'No date',
    'todo.tasksOne': 'task',
    'todo.tasksOther': 'tasks',
    'todo.confirmDelete': 'Delete task?',
    'todo.deleteGroupConfirm':
      'Delete this group? Tasks in it will be kept (without a group).',
    'todo.timeOptional': 'Time (optional)',
    'todo.showInCalendar': 'Show in calendar',
    'todo.labelColor': 'Label color',
    'todo.calendarTarget': 'Calendar',
    'todo.personalCalendar': 'Personal calendar',
    'todo.inUse': 'in use',
    'todo.calendarHint':
      'Tasks with a date appear in the calendar with a task icon in the selected color; completed ones are crossed out.',
    'todo.calendarSharedHint':
      'Members of this calendar can see these tasks.',

    // Dashboard widgets -----------------------------------------------------
    'dashboard.subtitle': "Welcome back! Here's your life overview.",
    'dashboard.customize': 'Customize',
    'dashboard.doneEditing': 'Done',
    'dashboard.reset': 'Reset layout',
    'dashboard.editHint': 'Drag widgets by their header, resize at the bottom-right corner, remove with the ×.',
    'dashboard.allDay': 'all day',
    'dashboard.emptyDay': 'Nothing planned for today.',
    'dashboard.emptyTodos': 'No open tasks.',
    'dashboard.emptyShopping': 'Shopping list is empty.',
    // Gallery
    'dashw.widget': 'Widget',
    'dashw.galleryTitle': 'Add widget',
    'dashw.searchWidgets': 'Search widgets',
    'dashw.noResults': 'Nothing found.',
    'dashw.styleCount': '{n} styles',
    'dashw.pickInstance': 'Which one?',
    'dashw.pickSize': 'Size',
    'dashw.resizeHint': 'You can resize it any time later.',
    'dashw.addWidget': 'Add widget',
    'dashw.removeWidget': 'Remove widget',
    'dashw.emptyDashboard': 'No widgets on the dashboard yet.',
    'dashw.resetConfirm': 'Reset the dashboard to the default widgets?',
    'dashw.sizeSmall': 'Small',
    'dashw.sizeMedium': 'Medium',
    'dashw.sizeLarge': 'Large',
    'dashw.myList': 'My list',
    'dashw.allAccounts': 'All accounts',
    'dashw.allSports': 'All sports',
    // Catalog: styles + descriptions
    'calendar.suggestLast': 'Last used {date}',
    'calendar.editingSeries': 'Editing a repeating event',
    'calendar.clickedOn': 'Clicked on',
    'calendar.seriesStart': 'Series start',
    'calendar.todosOf': "{name}'s to-dos",
    'calendar.fromTodosOf': 'from to-dos ({name})',
    'calendar.fromTodos': 'from to-dos',
    'dashw.calendarDay': 'Day view',
    'dashw.calendarDayDesc': "Today's events",
    'dashw.calendarWeek': 'Week view',
    'dashw.calendarWeekDesc': 'The whole week at a glance',
    'dashw.calendarMonth': 'Month view',
    'dashw.calendarMonthDesc': 'Full month as a grid',
    'dashw.calendarAgenda': 'Upcoming',
    'dashw.calendarAgendaDesc': 'List of the next events',
    'dashw.birthdaysUpcoming': 'Upcoming birthdays',
    'dashw.birthdaysUpcomingDesc': "Who's next to celebrate",
    'dashw.todoOpen': 'Open tasks',
    'dashw.todoOpenDesc': 'All open tasks, tickable',
    'dashw.todoToday': 'Due today',
    'dashw.todoTodayDesc': "Just today's tasks",
    'dashw.todoCount': 'Open count',
    'dashw.todoCountDesc': 'Just the number, nice and small',
    'dashw.shoppingList': 'Shopping list',
    'dashw.shoppingListDesc': 'Open items, tickable',
    'dashw.shoppingCount': 'Open items',
    'dashw.shoppingCountDesc': 'Just the count',
    'dashw.budgetRemaining': 'Left this month',
    'dashw.budgetRemainingDesc': "What's left this month",
    'dashw.budgetExpenses': 'Expenses',
    'dashw.budgetExpensesDesc': 'Total expenses this month',
    'dashw.budgetIncome': 'Income',
    'dashw.budgetIncomeDesc': 'Total income this month',
    'dashw.budgetCategories': 'Top categories',
    'dashw.budgetCategoriesDesc': 'Where the money goes this month',
    'dashw.budgetRecent': 'Recent entries',
    'dashw.budgetRecentDesc': 'The newest income and expenses',
    'dashw.savingsBalance': 'Balance',
    'dashw.savingsBalanceDesc': 'One account or all together',
    'dashw.savingsAccounts': 'All savings accounts',
    'dashw.savingsAccountsDesc': 'Every account with its balance',
    'dashw.fitnessWeek': 'Workouts this week',
    'dashw.fitnessWeekDesc': 'Number of sessions this week',
    'dashw.fitnessRecent': 'Recent workouts',
    'dashw.fitnessRecentDesc': 'Your latest logged sessions',
    'dashw.timeWeek': 'This week',
    'dashw.timeWeekDesc': 'Hours tracked this week',
    'dashw.timeRunning': 'Running timer',
    'dashw.timeRunningDesc': 'Shows live what is running',
    'dashw.diaryRecent': 'Recent entries',
    'dashw.diaryRecentDesc': 'Your newest diary entries',
    'dashw.fuelConsumption': 'Fuel',
    'dashw.fuelConsumptionDesc': 'Last fill-up and distance',
    'dashw.tutoringOpen': 'Outstanding payments',
    'dashw.tutoringOpenDesc': 'What students still owe',
    'dashw.projectsOverview': 'Overview',
    'dashw.projectsOverviewDesc': 'Profit or hours per project',
    // Widget bodies
    'dashw.noUpcoming': 'No upcoming events.',
    'dashw.tomorrow': 'Tomorrow',
    'dashw.inDays': 'in {n} days',
    'dashw.noBirthdays': 'No birthdays saved.',
    'dashw.openTasks': 'open tasks',
    'dashw.openItems': 'open items',
    'dashw.budget.remaining': 'Left this month',
    'dashw.budget.expenses': 'Expenses',
    'dashw.budget.income': 'Income',
    'dashw.budget.uncategorized': 'Uncategorized',
    'dashw.budget.noExpenses': 'No expenses this month',
    'dashw.budget.unnamed': 'Unnamed',
    'dashw.budget.noBookings': 'No entries this month',
    'dashw.savings.total': 'Total savings',
    'dashw.savings.totalRow': 'Total',
    'dashw.savings.noAccounts': 'No savings accounts yet',
    'dashw.savings.unnamedAccount': 'Account',
    'dashw.savings.accountOne': '1 account',
    'dashw.savings.accountCount': '{count} accounts',
    'dashw.fitness.empty': 'No workouts yet',
    'dashw.fitness.weekCount': 'Workouts this week',
    'dashw.fitness.weekDuration': '{time} total',
    'dashw.timetracker.empty': 'No time tracked yet',
    'dashw.timetracker.week': 'This week',
    'dashw.timetracker.running': 'running',
    'dashw.timetracker.entriesOne': '1 entry',
    'dashw.timetracker.entriesMany': '{n} entries',
    'dashw.timetracker.noTimer': 'No timer running',
    'dashw.timetracker.untitled': 'Untitled',
    'dashw.timetracker.noCompany': 'No company',
    'dashw.timetracker.since': 'since {time}',
    'dashw.diary.empty': 'No entries yet',
    'dashw.diary.noText': 'No text',
    'dashw.fuel.error': 'Fuel data unavailable',
    'dashw.fuel.empty': 'No fuel entries yet',
    'dashw.fuel.lastFillUp': 'Last fill-up',
    'dashw.fuel.totalKm': 'Kilometres driven',
    'dashw.fuel.lastEntry': 'Last entry {date}',
    'dashw.tutoring.error': 'Tutoring data unavailable',
    'dashw.tutoring.empty': 'No students yet',
    'dashw.tutoring.open': 'Outstanding',
    'dashw.tutoring.allSettled': 'All settled',
    'dashw.tutoring.oneStudent': '1 student owes',
    'dashw.tutoring.nStudents': '{count} students owe',
    'dashw.projects.error': 'Projects unavailable',
    'dashw.projects.empty': 'No projects yet',
    'dashw.projects.missing': 'Project not found',
    'dashw.projects.timeProject': 'Time project',
    'dashw.projects.oneEntry': '1 entry',
    'dashw.projects.nEntries': '{count} entries',

    // Budget ---------------------------------------------------------------
    'budget.titleMonth': 'Budget - {month}',
    'budget.overviewYear': 'Budget Overview - {year}',
    'budget.addMonth': 'Add Month',
    'budget.addMonthToYear': 'Add New Month to {year}',
    'budget.selectMonth': 'Select Month',
    'budget.manageCategories': 'Manage Categories',
    'budget.totalIncome': 'Total Income',
    'budget.totalExpenses': 'Total Expenses',
    'budget.totalSavings': 'Total Savings',
    'budget.remainingBalance': 'Remaining Balance',
    'budget.yearlyExpensesByCategory': 'Yearly Expenses by Category',
    'budget.expensesByCategoryMonth': 'Expenses by Category - {month}',
    'budget.noExpensesYear': 'No expenses recorded this year',
    'budget.noExpensesMonth': 'No expenses recorded for {month}',
    'budget.income': 'Income',
    'budget.expenses': 'Expenses',
    'budget.savings': 'Savings',
    'budget.noIncomeYear': 'No income recorded this year',
    'budget.noIncomeMonth': 'No income recorded for {month}',
    'budget.noSavingsYear': 'No savings recorded this year',
    'budget.noSavingsMonth': 'No savings recorded for {month}',
    'budget.addIncome': 'Add Income',
    'budget.addExpense': 'Add Expense',
    'budget.addSavings': 'Add Savings',
    'budget.addToSavings': 'Add to Savings',
    'budget.unnamed': 'Unnamed',
    'budget.unknownAccount': 'Unknown Account',
    'budget.unknownCategory': 'Unknown',
    'budget.name': 'Name',
    'budget.amount': 'Amount',
    'budget.category': 'Category',
    'budget.subCategory': 'Sub-Category',
    'budget.account': 'Account',
    'budget.additionalInfo': 'Additional Info',
    'budget.additionalInfoPlaceholder': 'Additional information…',
    'budget.selectCategory': 'Select Category',
    'budget.selectSubCategory': 'Select Sub-Category (optional)',
    'budget.selectSavingsAccount': 'Select Savings Account',
    'budget.noSavingsAccounts': 'No savings accounts found. Create one in Budget → Savings.',
    'budget.addInfo': '+ Add Info',
    'budget.hideInfo': 'Hide Info',
    'budget.tabExpense': 'Expense',
    'budget.tabIncome': 'Income',
    'budget.tabSavings': 'Savings',
    'budget.addNewExpenseCategory': 'Add New Expense Category',
    'budget.addNewIncomeCategory': 'Add New Income Category',
    'budget.addNewSavingsCategory': 'Add New Savings Category',
    'budget.subCategoriesCount': '({count} sub-categories)',
    'budget.viewDetails': 'View Details',
    'budget.deleteCategory': 'Delete category',
    'budget.deleteCategoryConfirm': 'Delete "{name}" category?',
    'budget.newSubCategoryPlaceholder': 'New sub-category',
    'budget.categoryNamePlaceholder': 'Category name',
    'budget.colorLabel': 'Color:',
    'budget.addCategory': 'Add Category',
    'budget.categoryDetails': '{name} Details',
    'budget.noSubCategories': 'No sub-categories defined',
    'budget.totalCategory': 'Total Category:',
    'budget.expenseDetails': 'Expense Details',
    'budget.incomeDetails': 'Income Details',
    'budget.savingsDetails': 'Savings Details',
    'budget.editExpense': 'Edit Expense',
    'budget.editIncome': 'Edit Income',
    'budget.editSavings': 'Edit Savings',
    'budget.saveChanges': 'Save Changes',
    'budget.categorySubcategories': '{name} - Subcategories',
    'budget.pieHint': 'Click or hover over a segment to see details',
    'budget.otherSubCategory': 'Other',
    'budget.noData': 'No data available',
    'budget.total': 'Total',

    // Savings accounts -----------------------------------------------------
    'savings.title': 'Savings Accounts',
    'savings.subtitle': 'Manage your bank accounts and investments',
    'savings.transfer': 'Transfer',
    'savings.addAccount': 'Add Account',
    'savings.totalSavings': 'Total Savings',
    'savings.accountCount': 'Number of Accounts',
    'savings.budgetSavings': 'Budget Savings',
    'savings.bankAccounts': 'Bank Accounts',
    'savings.balance': 'Balance',
    'savings.addAccountTitle': 'Add Savings Account',
    'savings.accountNamePlaceholder': 'Account Name (e.g., Raiffeisen Bank)',
    'savings.accountName': 'Account Name',
    'savings.typeBank': 'Bank Account',
    'savings.typeInvestment': 'Investment',
    'savings.typeCash': 'Cash',
    'savings.typeOther': 'Other',
    'savings.initialBalance': 'Initial Balance',
    'savings.description': 'Description',
    'savings.descriptionOptional': 'Description (optional)',
    'savings.editAccount': 'Edit Account',
    'savings.saveChanges': 'Save Changes',
    'savings.transferTitle': 'Transfer Between Accounts',
    'savings.fromAccount': 'From Account',
    'savings.toAccount': 'To Account',
    'savings.amount': 'Amount',
    'savings.insufficientBalance': 'Insufficient balance for transfer',
    'savings.deleteConfirm': 'Are you sure you want to delete this account?',
    'savings.totalBalance': 'Total Balance',
    'savings.base': 'Base: {amount}',
    'savings.fromBudget': '+ {amount} from budget',
    'savings.addTransaction': 'Add Transaction',
    'savings.depositOption': 'Deposit (+)',
    'savings.withdrawalOption': 'Withdrawal (-)',
    'savings.transactionHistory': 'Transaction History',
    'savings.linkedSavings': 'Linked Savings',
    'savings.savingsEntry': 'Savings',
    'savings.deposit': 'Deposit',
    'savings.withdrawal': 'Withdrawal',
    'savings.noTransactions': 'No transactions yet. Add your first deposit or withdrawal above.',
    'savings.noLinkedSavings': 'No linked savings from budget.',
    'savings.badgeBudget': 'Budget',

    // Placeholders --------------------------------------------------------
    'placeholder.comingSoon': 'Coming soon'
  },

  de: {
    // App / shell ---------------------------------------------------------
    'app.title': 'Life Manager',
    'app.tagline': 'Track, Budget & Plan',
    'app.loading': 'Lädt…',
    'app.settings': 'Einstellungen',
    'app.signOut': 'Abmelden',
    'app.syncError': 'Sync-Fehler: {message}',

    // Common --------------------------------------------------------------
    'common.save': 'Speichern',
    'common.cancel': 'Abbrechen',
    'common.delete': 'Löschen',
    'common.edit': 'Bearbeiten',
    'common.add': 'Hinzufügen',
    'common.back': 'Zurück',
    'common.confirm': 'Bestätigen',
    'common.close': 'Schließen',
    'common.yes': 'Ja',
    'common.no': 'Nein',
    'common.loading': 'Lädt…',
    'common.uploading': 'Lädt hoch…',
    'common.search': 'Suchen',
    'common.today': 'Heute',
    'common.yesterday': 'Gestern',
    'widget.openApp': 'Eulea öffnen, um den Kalender zu laden.',
    'common.tomorrow': 'Morgen',
    'common.upcoming': 'Bevorstehend',
    'common.all': 'Alle',
    'common.completed': 'Erledigt',
    'common.untitled': 'Ohne Titel',
    'common.optional': 'optional',
    'common.note': 'Notiz',
    'common.priority': 'Priorität',
    'common.date': 'Datum',
    'common.title': 'Titel',

    // Auth ----------------------------------------------------------------
    'auth.signInTitle': 'Bei deinem Konto anmelden',
    'auth.signUpTitle': 'Neues Konto erstellen',
    'auth.email': 'E-Mail',
    'auth.password': 'Passwort',
    'auth.signIn': 'Anmelden',
    'auth.signUp': 'Registrieren',
    'auth.noAccount': 'Noch kein Konto?',
    'auth.haveAccount': 'Schon ein Konto?',
    'auth.pleaseWait': 'Bitte warten…',
    'auth.failed': 'Anmeldung fehlgeschlagen',
    'auth.confirmEmail':
      'Konto erstellt. Bestätige deine E-Mail-Adresse und melde dich dann an.',
    'onboarding.nameTitle': 'Willkommen!',
    'onboarding.nameHint': 'Wähle einen Anzeigenamen. Er wird Mitgliedern geteilter Kalender angezeigt.',
    'onboarding.continue': 'Weiter',

    // Settings ------------------------------------------------------------
    'settings.title': 'Einstellungen',
    'settings.menuOrder': 'Reihenfolge im Menü',
    'settings.menuOrderHint':
      'Verschiebe die Hauptgruppen mit den Pfeilen nach oben oder unten.',
    'settings.enabledFeatures': 'Aktivierte Funktionen',
    'settings.enabledFeaturesHint':
      'Schalte Funktionen einzeln ein oder aus. Klicke auf eine Funktion für mehr Informationen.',
    'settings.moveUp': 'Nach oben',
    'settings.moveDown': 'Nach unten',
    'settings.localFound': 'Lokale Daten gefunden',
    'settings.localFoundHint':
      'Du hast Daten aus der lokalen Version dieser App in deinem Browser. Klicke auf den Button, um sie in die Cloud zu importieren.',
    'settings.importLocal': 'Lokale Daten in Cloud importieren',
    'settings.importing': 'Importiere…',
    'settings.importSuccess': '✅ Import erfolgreich!',
    'settings.importFailed': '❌ Import fehlgeschlagen.',
    'settings.confirmMigrate':
      'Daten aus dem lokalen Browser-Speicher in die Cloud importieren? Bestehende Cloud-Daten dieses Kontos werden mit den lokalen Daten zusammengeführt (gleiche IDs werden überschrieben).',
    'settings.backup': 'Datensicherung',
    'settings.backupHint':
      'Exportiere alle deine Daten als JSON-Datei oder importiere ein vorheriges Backup.',
    'settings.exportJson': 'JSON exportieren',
    'settings.importJson': 'JSON importieren',
    'settings.language': 'Sprache',
    'settings.languageHint': 'Wähle die Sprache für die gesamte App.',
    'settings.languageEnglish': 'Englisch',
    'settings.languageGerman': 'Deutsch',
    'settings.appearance': 'Darstellung',
    'settings.appearanceHint': 'Wähle Theme und eine persönliche Akzentfarbe.',
    'settings.themeMode': 'Modus',
    'settings.themeLight': 'Hell',
    'settings.themeDark': 'Dunkel',
    'settings.themeSystem': 'System',
    'settings.primaryColor': 'Akzentfarbe',
    'settings.showPersonalCalendar': 'Persönlichen Kalender anzeigen',
    'settings.showPersonalCalendarHint': 'Blendet den persönlichen Kalender in der Seitenleiste aus (Daten bleiben erhalten).',
    'settings.sharedCalendarsVisibility': 'Geteilte Kalender',
    'settings.profile': 'Profil',
    'settings.profileHint': 'Dein Name und Avatar werden Mitgliedern geteilter Kalender angezeigt.',
    'settings.profileName': 'Anzeigename',
    'settings.profileNamePlaceholder': 'z. B. Jana',
    'settings.profileNoName': 'Kein Name gesetzt',
    'settings.profileColor': 'Avatar-Farbe',
    'settings.profileEmoji': 'Avatar',
    'settings.profileEmojiNone': 'Anfangsbuchstabe verwenden',
    'settings.profileSaved': 'Gespeichert',
    'settings.profileVisibility': 'Für Kalendermitglieder sichtbar',
    'settings.profileKindEmoji': 'Avatar',
    'settings.profileKindPhoto': 'Foto',
    'settings.profilePhotoUpload': 'Foto hochladen',
    'settings.profilePhotoReplace': 'Foto ersetzen',
    'settings.profilePhotoUploading': 'Wird hochgeladen…',
    'settings.profilePhotoRemove': 'Foto entfernen',
    'settings.profilePhotoHint': 'JPG oder PNG, max. 5 MB. Für Kalendermitglieder sichtbar.',
    'settings.profilePhotoTooLarge': 'Bild ist zu groß (max. 5 MB).',
    'sharedCalendar.createTitle': 'Geteilten Kalender erstellen',
    'sharedCalendar.namePlaceholder': 'Kalendername (z. B. Familie)',
    'sharedCalendar.create': 'Erstellen',
    'sharedCalendar.createButton': 'Geteilten Kalender erstellen',
    'sharedCalendar.shareTitle': 'Diesen Kalender teilen',
    'sharedCalendar.shareHint': 'Jeder angemeldete Nutzer, der diesen Link öffnet, wird als Mitglied hinzugefügt.',
    'sharedCalendar.copyLink': 'Link kopieren',
    'sharedCalendar.copied': 'Kopiert!',
    'sharedCalendar.delete': 'Kalender löschen',
    'sharedCalendar.deleteConfirm': 'Diesen geteilten Kalender für alle löschen? Kann nicht rückgängig gemacht werden.',
    'sharedCalendar.leave': 'Kalender verlassen',
    'sharedCalendar.leaveConfirm': 'Diesen geteilten Kalender verlassen? Du verlierst den Zugriff auf die Termine.',
    'sharedCalendar.joinSuccess': 'Geteilter Kalender beigetreten',
    'sharedCalendar.joinFailed': 'Kalender konnte nicht beigetreten werden: {message}',
    'sharedCalendar.owner': 'Besitzer',
    'sharedCalendar.member': 'Mitglied',
    'sharedCalendar.settings': 'Kalendereinstellungen',
    'sharedCalendar.membersTitle': 'Mitglieder',
    'sharedCalendar.invite': 'Einladungslink',
    'sharedCalendar.loadingMembers': 'Mitglieder werden geladen…',
    'sharedCalendar.you': 'Du',
    'sharedCalendar.createdBy': 'Erstellt von',
    'sharedCalendar.unknownUser': 'Unbekannter Nutzer',
    'sharedCalendar.participants': 'Teilnehmer',
    'sharedCalendar.addParticipant': 'Hinzufügen…',
    'sharedCalendar.allAdded': 'Alle Mitglieder sind dabei.',
    'sharedCalendar.withBirthdays': 'Geburtstagsliste hinzufügen',
    'sharedCalendar.withBirthdaysHint': 'Eine separate Geburtstagsliste nur für diesen Kalender. Alle Mitglieder können Einträge hinzufügen und bearbeiten.',
    'sharedCalendar.birthdays': 'Geburtstage',
    'sharedCalendar.manageBirthdays': 'Geburtstage verwalten',
    'sharedCalendar.birthdaysSection': 'Geburtstagsliste',
    'sharedCalendar.enableBirthdays': 'Geburtstagsliste aktivieren',
    'sharedCalendar.enableBirthdaysHint': 'Für alle Mitglieder dieses Kalenders sichtbar.',
    'sharedCalendar.backToCalendar': 'Zurück zum Kalender',
    'color.blue': 'Blau',
    'color.lightBlue': 'Hellblau',
    'color.green': 'Grün',
    'color.pink': 'Pink',
    'color.lightPink': 'Hellpink',
    'color.yellow': 'Gelb',
    'color.orange': 'Orange',
    'color.black': 'Schwarz',
    'color.white': 'Weiß',

    // Sidebar / feature groups --------------------------------------------
    'nav.more': 'Mehr',
    'nav.allPages': 'Alle Seiten',
    'settings.account': 'Dein Konto',
    'settings.deleteAccount': 'Konto löschen',
    'settings.deletingAccount': 'Wird gelöscht…',
    'settings.deleteAccountHint':
      'Beim Löschen verschwinden dein Login und alles darin — Kalender, Budget, Tagebuch, Fotos. Das lässt sich nicht rückgängig machen.',
    'settings.deleteAccountConfirm':
      'Konto und alle Daten endgültig löschen? Das lässt sich nicht rückgängig machen.',
    'settings.deleteAccountTypeTitle': 'Zum Bestätigen LÖSCHEN eingeben',
    'settings.deleteAccountWord': 'LÖSCHEN',
    'settings.deleteAccountFailed': 'Konto konnte nicht gelöscht werden: {message}',
    'settings.tabBar': 'Tab-Leiste',
    'settings.tabBarHint': 'Wähle bis zu {n} Seiten für die Leiste am unteren Bildschirmrand. Alles andere bleibt unter „Mehr“.',
    'nav.dashboard': 'Dashboard',
    'nav.planning': 'Planung',
    'nav.calendar': 'Kalender',
    'nav.birthdays': 'Geburtstagskalender',
    'nav.todo': 'To-dos',
    'nav.reminders': 'Erinnerungen',
    'nav.finance': 'Finanzen',
    'nav.budget': 'Budget',
    'nav.savings': 'Sparen',
    'nav.fitness': 'Fitness',
    'nav.fitnessOverview': 'Übersicht',
    'nav.sports': 'Sportarten',
    'nav.personal': 'Persönlich',
    'nav.diary': 'Tagebuch',
    'nav.habits': 'Gewohnheiten',
    'nav.work': 'Arbeit',
    'nav.timetracker': 'Zeiterfassung',
    'nav.shopping': 'Einkaufsliste',
    'nav.fuel': 'Tank-Liste',
    'nav.tutoring': 'Nachhilfe',
    'nav.projects': 'Projekte',

    'navDesc.dashboard':
      'Übersichtliche Wochenansicht mit allen anstehenden Terminen, Geburtstagen und einem schnellen Blick auf deine wichtigsten Daten.',
    'navDesc.planning':
      'Alles rund um Planung: Kalender, Geburtstage, Aufgaben und Erinnerungen.',
    'navDesc.calendar': 'Monatsansicht mit Events, farbigen Labels und Wiederholungen.',
    'navDesc.birthdays':
      'Verwalte Geburtstage separat und synchronisiere sie mit dem Hauptkalender.',
    'navDesc.todo': 'Einfache To-do-Liste für deine Aufgaben.',
    'navDesc.reminders': 'Erinnerungen für wichtige Dinge.',
    'navDesc.finance': 'Alles rund um Geld: Budget, Sparen und Konten.',
    'navDesc.budget': 'Einnahmen und Ausgaben dieses Monats und Trend.',
    'navDesc.savings': 'Sparziele, Konten und Sparquoten verfolgen.',
    'navDesc.fitness':
      'Tracke Workouts, persönliche Bestleistungen und plane wiederkehrende Trainings über den Kalender.',
    'navDesc.fitnessOverview': 'Aktive Sportarten und Statistiken.',
    'navDesc.sports':
      'Detail-Ansichten je Sportart inkl. Workouts, PRs und Workout-Strukturen.',
    'navDesc.personal': 'Persönliches: Tagebuch, Gewohnheiten und mehr.',
    'navDesc.diary': 'Persönliches Tagebuch.',
    'navDesc.habits': 'Gewohnheiten verfolgen und Streaks aufbauen.',
    'navDesc.work': 'Tools für die Arbeit: Zeiterfassung und mehr.',
    'navDesc.timetracker': 'Arbeitszeit erfassen.',
    'navDesc.shopping': 'Einkaufslisten mit Rezepten und Mengenberechnung.',
    'navDesc.fuel': 'Geteilte Tank-Liste: Kilometerstände festhalten und Tankkosten nach gefahrenen Kilometern aufteilen.',
    'navDesc.tutoring': 'Nachhilfe-Sitzungen pro Schüler und Semester erfassen. Bezahlt-Status pro Sitzung abhaken.',
    'navDesc.projects': 'Pro Projekt Ausgaben & Einnahmen erfassen, kumulative Bilanz und Break-even sehen.',

    // Diary ---------------------------------------------------------------
    'diary.title': 'Tagebuch',
    'diary.subtitle': 'Persönliches Tagebuch',
    'diary.newEntry': 'Neuer Eintrag',
    'diary.firstEntry': 'Ersten Eintrag erstellen',
    'diary.empty': 'Noch keine Einträge. Beginne dein Tagebuch!',
    'diary.titlePlaceholder': 'Titel…',
    'diary.bodyPlaceholder': 'Schreib hier deinen Eintrag…',
    'diary.addPhoto': 'Foto hinzufügen',
    'diary.removePhoto': 'Foto entfernen',
    'diary.deleteConfirm': 'Eintrag löschen?',
    'diary.morePhotos': '+{count} weitere Fotos',
    'diary.uploadFailed': 'Foto-Upload fehlgeschlagen: {message}',
    'diary.showTitleInList': 'Titel in der Liste anzeigen',
    'diary.showTitleInListHint': 'Wenn aus, zeigt die Liste für diesen Eintrag nur das Datum.',

    // Todo ----------------------------------------------------------------
    'todo.title': 'To-dos',
    'todo.subtitle': 'To-dos verwalten',
    'todo.newTaskPlaceholder': 'Was muss erledigt werden?',
    'todo.notePlaceholder': 'Zusätzliche Infos…',
    'todo.allGroups': 'Alle Gruppen',
    'todo.newGroup': 'Neue Gruppe',
    'todo.newGroupShort': 'Neu',
    'todo.manageGroups': 'Gruppen verwalten',
    'todo.groupNamePrompt': 'Name der neuen Gruppe',
    'todo.priorityHigh': 'Hoch',
    'todo.priorityMedium': 'Mittel',
    'todo.priorityLow': 'Niedrig',
    'todo.viewToday': 'Heute',
    'todo.viewUpcoming': 'Demnächst',
    'todo.viewAll': 'Alle',
    'todo.viewCompleted': 'Erledigt',
    'todo.editTitle': 'Aufgabe bearbeiten',
    'todo.newTitle': 'Neue Aufgabe',
    'todo.dateOptional': 'Datum (optional)',
    'todo.priorityOptional': 'Priorität (optional)',
    'todo.groupOptional': 'Gruppe (optional)',
    'todo.noteOptional': 'Notiz (optional)',
    'todo.noGroup': 'Keine',
    'todo.markDone': 'Erledigt',
    'todo.markOpen': 'Als offen markieren',
    'todo.overdue': 'überfällig',
    'todo.emptyToday': 'Keine Aufgaben für heute.',
    'todo.emptyUpcoming': 'Keine bevorstehenden Aufgaben.',
    'todo.emptyAll': 'Noch keine Aufgaben.',
    'todo.emptyCompleted': 'Noch nichts erledigt.',
    'todo.noGroups': 'Noch keine Gruppen.',
    'todo.noDate': 'Ohne Datum',
    'todo.tasksOne': 'Aufgabe',
    'todo.tasksOther': 'Aufgaben',
    'todo.confirmDelete': 'Aufgabe löschen?',
    'todo.deleteGroupConfirm':
      'Gruppe löschen? Die Aufgaben bleiben erhalten (ohne Gruppe).',
    'todo.timeOptional': 'Uhrzeit (optional)',
    'todo.showInCalendar': 'Im Kalender anzeigen',
    'todo.labelColor': 'Label-Farbe',
    'todo.calendarTarget': 'Kalender',
    'todo.personalCalendar': 'Persönlicher Kalender',
    'todo.inUse': 'belegt',
    'todo.calendarHint':
      'Aufgaben mit Datum erscheinen im Kalender mit Aufgaben-Symbol in der gewählten Farbe; erledigte werden durchgestrichen.',
    'todo.calendarSharedHint':
      'Mitglieder dieses Kalenders sehen diese Aufgaben.',

    // Dashboard widgets -----------------------------------------------------
    'dashboard.subtitle': 'Willkommen zurück! Dein Überblick.',
    'dashboard.customize': 'Anpassen',
    'dashboard.doneEditing': 'Fertig',
    'dashboard.reset': 'Layout zurücksetzen',
    'dashboard.editHint': 'Widgets am Kopf ziehen, unten rechts in der Größe ändern, mit × entfernen.',
    'dashboard.allDay': 'ganztägig',
    'dashboard.emptyDay': 'Heute nichts geplant.',
    'dashboard.emptyTodos': 'Keine offenen Aufgaben.',
    'dashboard.emptyShopping': 'Einkaufsliste ist leer.',
    // Galerie
    'dashw.widget': 'Widget',
    'dashw.galleryTitle': 'Widget hinzufügen',
    'dashw.searchWidgets': 'In Widgets suchen',
    'dashw.noResults': 'Nichts gefunden.',
    'dashw.styleCount': '{n} Stile',
    'dashw.pickInstance': 'Welche(n)?',
    'dashw.pickSize': 'Größe',
    'dashw.resizeHint': 'Die Größe lässt sich später jederzeit ändern.',
    'dashw.addWidget': 'Widget hinzufügen',
    'dashw.removeWidget': 'Widget entfernen',
    'dashw.emptyDashboard': 'Noch keine Widgets auf dem Dashboard.',
    'dashw.resetConfirm': 'Dashboard auf die Standard-Widgets zurücksetzen?',
    'dashw.sizeSmall': 'Klein',
    'dashw.sizeMedium': 'Mittel',
    'dashw.sizeLarge': 'Groß',
    'dashw.myList': 'Meine Liste',
    'dashw.allAccounts': 'Alle Konten',
    'dashw.allSports': 'Alle Sportarten',
    // Katalog: Stile + Beschreibungen
    'calendar.suggestLast': 'Zuletzt {date}',
    'calendar.editingSeries': 'Serie wird bearbeitet',
    'calendar.clickedOn': 'Geklickt auf',
    'calendar.seriesStart': 'Serien‑Start',
    'calendar.todosOf': '{name}s To-dos',
    'calendar.fromTodosOf': 'aus To-dos ({name})',
    'calendar.fromTodos': 'aus To-dos',
    'dashw.calendarDay': 'Tagesansicht',
    'dashw.calendarDayDesc': 'Termine von heute',
    'dashw.calendarWeek': 'Wochenansicht',
    'dashw.calendarWeekDesc': 'Die ganze Woche auf einen Blick',
    'dashw.calendarMonth': 'Monatsübersicht',
    'dashw.calendarMonthDesc': 'Ganzer Monat als Raster',
    'dashw.calendarAgenda': 'Nächste Termine',
    'dashw.calendarAgendaDesc': 'Liste der kommenden Termine',
    'dashw.birthdaysUpcoming': 'Nächste Geburtstage',
    'dashw.birthdaysUpcomingDesc': 'Wer als Nächstes Geburtstag hat',
    'dashw.todoOpen': 'Offene Aufgaben',
    'dashw.todoOpenDesc': 'Alle offenen Aufgaben, abhakbar',
    'dashw.todoToday': 'Heute fällig',
    'dashw.todoTodayDesc': 'Nur die Aufgaben von heute',
    'dashw.todoCount': 'Anzahl offen',
    'dashw.todoCountDesc': 'Nur die Zahl, ganz klein',
    'dashw.shoppingList': 'Einkaufsliste',
    'dashw.shoppingListDesc': 'Offene Artikel, abhakbar',
    'dashw.shoppingCount': 'Offene Artikel',
    'dashw.shoppingCountDesc': 'Nur die Anzahl',
    'dashw.budgetRemaining': 'Übrig diesen Monat',
    'dashw.budgetRemainingDesc': 'Was diesen Monat noch übrig ist',
    'dashw.budgetExpenses': 'Ausgaben',
    'dashw.budgetExpensesDesc': 'Summe der Ausgaben diesen Monat',
    'dashw.budgetIncome': 'Einnahmen',
    'dashw.budgetIncomeDesc': 'Summe der Einnahmen diesen Monat',
    'dashw.budgetCategories': 'Top-Kategorien',
    'dashw.budgetCategoriesDesc': 'Wofür das Geld diesen Monat weggeht',
    'dashw.budgetRecent': 'Letzte Buchungen',
    'dashw.budgetRecentDesc': 'Die neuesten Einnahmen und Ausgaben',
    'dashw.savingsBalance': 'Kontostand',
    'dashw.savingsBalanceDesc': 'Stand eines Kontos oder aller zusammen',
    'dashw.savingsAccounts': 'Alle Sparkonten',
    'dashw.savingsAccountsDesc': 'Liste aller Konten mit Stand',
    'dashw.fitnessWeek': 'Workouts diese Woche',
    'dashw.fitnessWeekDesc': 'Anzahl Trainings diese Woche',
    'dashw.fitnessRecent': 'Letzte Workouts',
    'dashw.fitnessRecentDesc': 'Die zuletzt eingetragenen Trainings',
    'dashw.timeWeek': 'Diese Woche',
    'dashw.timeWeekDesc': 'Erfasste Stunden diese Woche',
    'dashw.timeRunning': 'Laufender Timer',
    'dashw.timeRunningDesc': 'Zeigt live, was gerade läuft',
    'dashw.diaryRecent': 'Letzte Einträge',
    'dashw.diaryRecentDesc': 'Die neuesten Tagebucheinträge',
    'dashw.fuelConsumption': 'Tanken',
    'dashw.fuelConsumptionDesc': 'Letzter Tankstopp und Kilometer',
    'dashw.tutoringOpen': 'Offene Zahlungen',
    'dashw.tutoringOpenDesc': 'Was Schüler noch schulden',
    'dashw.projectsOverview': 'Übersicht',
    'dashw.projectsOverviewDesc': 'Gewinn bzw. Stunden je Projekt',
    // Widget-Inhalte
    'dashw.noUpcoming': 'Keine kommenden Termine.',
    'dashw.tomorrow': 'Morgen',
    'dashw.inDays': 'in {n} Tagen',
    'dashw.noBirthdays': 'Keine Geburtstage eingetragen.',
    'dashw.openTasks': 'offene Aufgaben',
    'dashw.openItems': 'offene Artikel',
    'dashw.budget.remaining': 'Übrig diesen Monat',
    'dashw.budget.expenses': 'Ausgaben',
    'dashw.budget.income': 'Einnahmen',
    'dashw.budget.uncategorized': 'Ohne Kategorie',
    'dashw.budget.noExpenses': 'Keine Ausgaben in diesem Monat',
    'dashw.budget.unnamed': 'Ohne Namen',
    'dashw.budget.noBookings': 'Keine Buchungen in diesem Monat',
    'dashw.savings.total': 'Sparen gesamt',
    'dashw.savings.totalRow': 'Gesamt',
    'dashw.savings.noAccounts': 'Noch keine Sparkonten',
    'dashw.savings.unnamedAccount': 'Konto',
    'dashw.savings.accountOne': '1 Konto',
    'dashw.savings.accountCount': '{count} Konten',
    'dashw.fitness.empty': 'Noch keine Workouts',
    'dashw.fitness.weekCount': 'Workouts diese Woche',
    'dashw.fitness.weekDuration': '{time} gesamt',
    'dashw.timetracker.empty': 'Noch keine Zeiten erfasst',
    'dashw.timetracker.week': 'Diese Woche',
    'dashw.timetracker.running': 'läuft',
    'dashw.timetracker.entriesOne': '1 Eintrag',
    'dashw.timetracker.entriesMany': '{n} Einträge',
    'dashw.timetracker.noTimer': 'Kein Timer aktiv',
    'dashw.timetracker.untitled': 'Ohne Titel',
    'dashw.timetracker.noCompany': 'Ohne Firma',
    'dashw.timetracker.since': 'seit {time}',
    'dashw.diary.empty': 'Noch keine Einträge',
    'dashw.diary.noText': 'Kein Text',
    'dashw.fuel.error': 'Tankdaten nicht geladen',
    'dashw.fuel.empty': 'Noch keine Tankeinträge',
    'dashw.fuel.lastFillUp': 'Letzter Tankstopp',
    'dashw.fuel.totalKm': 'Gefahrene km',
    'dashw.fuel.lastEntry': 'Letzter Eintrag {date}',
    'dashw.tutoring.error': 'Nachhilfe nicht geladen',
    'dashw.tutoring.empty': 'Noch keine Schüler',
    'dashw.tutoring.open': 'Offene Beträge',
    'dashw.tutoring.allSettled': 'Alles bezahlt',
    'dashw.tutoring.oneStudent': '1 Schüler offen',
    'dashw.tutoring.nStudents': '{count} Schüler offen',
    'dashw.projects.error': 'Projekte nicht geladen',
    'dashw.projects.empty': 'Noch keine Projekte',
    'dashw.projects.missing': 'Projekt nicht gefunden',
    'dashw.projects.timeProject': 'Zeit-Projekt',
    'dashw.projects.oneEntry': '1 Eintrag',
    'dashw.projects.nEntries': '{count} Einträge',

    // Budget ---------------------------------------------------------------
    'budget.titleMonth': 'Budget - {month}',
    'budget.overviewYear': 'Budget-Übersicht - {year}',
    'budget.addMonth': 'Monat hinzufügen',
    'budget.addMonthToYear': 'Neuen Monat zu {year} hinzufügen',
    'budget.selectMonth': 'Monat wählen',
    'budget.manageCategories': 'Kategorien verwalten',
    'budget.totalIncome': 'Einnahmen gesamt',
    'budget.totalExpenses': 'Ausgaben gesamt',
    'budget.totalSavings': 'Sparen gesamt',
    'budget.remainingBalance': 'Verbleibend',
    'budget.yearlyExpensesByCategory': 'Jahresausgaben nach Kategorie',
    'budget.expensesByCategoryMonth': 'Ausgaben nach Kategorie - {month}',
    'budget.noExpensesYear': 'Keine Ausgaben in diesem Jahr erfasst',
    'budget.noExpensesMonth': 'Keine Ausgaben für {month} erfasst',
    'budget.income': 'Einnahmen',
    'budget.expenses': 'Ausgaben',
    'budget.savings': 'Sparen',
    'budget.noIncomeYear': 'Keine Einnahmen in diesem Jahr erfasst',
    'budget.noIncomeMonth': 'Keine Einnahmen für {month} erfasst',
    'budget.noSavingsYear': 'Keine Sparbeträge in diesem Jahr erfasst',
    'budget.noSavingsMonth': 'Keine Sparbeträge für {month} erfasst',
    'budget.addIncome': 'Einnahme hinzufügen',
    'budget.addExpense': 'Ausgabe hinzufügen',
    'budget.addSavings': 'Sparbetrag hinzufügen',
    'budget.addToSavings': 'Zum Sparen hinzufügen',
    'budget.unnamed': 'Ohne Namen',
    'budget.unknownAccount': 'Unbekanntes Konto',
    'budget.unknownCategory': 'Unbekannt',
    'budget.name': 'Name',
    'budget.amount': 'Betrag',
    'budget.category': 'Kategorie',
    'budget.subCategory': 'Unterkategorie',
    'budget.account': 'Konto',
    'budget.additionalInfo': 'Zusätzliche Info',
    'budget.additionalInfoPlaceholder': 'Zusätzliche Informationen…',
    'budget.selectCategory': 'Kategorie wählen',
    'budget.selectSubCategory': 'Unterkategorie wählen (optional)',
    'budget.selectSavingsAccount': 'Sparkonto wählen',
    'budget.noSavingsAccounts': 'Keine Sparkonten gefunden. Lege eins unter Budget → Sparen an.',
    'budget.addInfo': '+ Info hinzufügen',
    'budget.hideInfo': 'Info ausblenden',
    'budget.tabExpense': 'Ausgaben',
    'budget.tabIncome': 'Einnahmen',
    'budget.tabSavings': 'Sparen',
    'budget.addNewExpenseCategory': 'Neue Ausgaben-Kategorie',
    'budget.addNewIncomeCategory': 'Neue Einnahmen-Kategorie',
    'budget.addNewSavingsCategory': 'Neue Spar-Kategorie',
    'budget.subCategoriesCount': '({count} Unterkategorien)',
    'budget.viewDetails': 'Details ansehen',
    'budget.deleteCategory': 'Kategorie löschen',
    'budget.deleteCategoryConfirm': 'Kategorie „{name}“ löschen?',
    'budget.newSubCategoryPlaceholder': 'Neue Unterkategorie',
    'budget.categoryNamePlaceholder': 'Name der Kategorie',
    'budget.colorLabel': 'Farbe:',
    'budget.addCategory': 'Kategorie hinzufügen',
    'budget.categoryDetails': '{name} – Details',
    'budget.noSubCategories': 'Keine Unterkategorien definiert',
    'budget.totalCategory': 'Kategorie gesamt:',
    'budget.expenseDetails': 'Details zur Ausgabe',
    'budget.incomeDetails': 'Details zur Einnahme',
    'budget.savingsDetails': 'Details zum Sparbetrag',
    'budget.editExpense': 'Ausgabe bearbeiten',
    'budget.editIncome': 'Einnahme bearbeiten',
    'budget.editSavings': 'Sparbetrag bearbeiten',
    'budget.saveChanges': 'Änderungen speichern',
    'budget.categorySubcategories': '{name} - Unterkategorien',
    'budget.pieHint': 'Klicke auf ein Segment oder fahre darüber, um Details zu sehen',
    'budget.otherSubCategory': 'Sonstiges',
    'budget.noData': 'Keine Daten vorhanden',
    'budget.total': 'Gesamt',

    // Sparkonten -----------------------------------------------------------
    'savings.title': 'Sparkonten',
    'savings.subtitle': 'Verwalte deine Bankkonten und Anlagen',
    'savings.transfer': 'Umbuchen',
    'savings.addAccount': 'Konto hinzufügen',
    'savings.totalSavings': 'Sparen gesamt',
    'savings.accountCount': 'Anzahl Konten',
    'savings.budgetSavings': 'Sparen aus Budget',
    'savings.bankAccounts': 'Bankkonten',
    'savings.balance': 'Kontostand',
    'savings.addAccountTitle': 'Sparkonto hinzufügen',
    'savings.accountNamePlaceholder': 'Kontoname (z. B. Raiffeisen Bank)',
    'savings.accountName': 'Kontoname',
    'savings.typeBank': 'Bankkonto',
    'savings.typeInvestment': 'Anlage',
    'savings.typeCash': 'Bargeld',
    'savings.typeOther': 'Sonstiges',
    'savings.initialBalance': 'Anfangsbestand',
    'savings.description': 'Beschreibung',
    'savings.descriptionOptional': 'Beschreibung (optional)',
    'savings.editAccount': 'Konto bearbeiten',
    'savings.saveChanges': 'Änderungen speichern',
    'savings.transferTitle': 'Umbuchung zwischen Konten',
    'savings.fromAccount': 'Von Konto',
    'savings.toAccount': 'Auf Konto',
    'savings.amount': 'Betrag',
    'savings.insufficientBalance': 'Nicht genug Guthaben für die Umbuchung',
    'savings.deleteConfirm': 'Möchtest du dieses Konto wirklich löschen?',
    'savings.totalBalance': 'Gesamtstand',
    'savings.base': 'Basis: {amount}',
    'savings.fromBudget': '+ {amount} aus dem Budget',
    'savings.addTransaction': 'Buchung hinzufügen',
    'savings.depositOption': 'Einzahlung (+)',
    'savings.withdrawalOption': 'Auszahlung (-)',
    'savings.transactionHistory': 'Buchungsverlauf',
    'savings.linkedSavings': 'Verknüpfte Sparbeträge',
    'savings.savingsEntry': 'Sparen',
    'savings.deposit': 'Einzahlung',
    'savings.withdrawal': 'Auszahlung',
    'savings.noTransactions': 'Noch keine Buchungen. Trage oben deine erste Ein- oder Auszahlung ein.',
    'savings.noLinkedSavings': 'Keine verknüpften Sparbeträge aus dem Budget.',
    'savings.badgeBudget': 'Budget',

    // Placeholders --------------------------------------------------------
    'placeholder.comingSoon': 'Demnächst verfügbar'
  }
};

const I18nContext = createContext({
  lang: 'de',
  setLang: () => {},
  t: (k) => k,
  locale: de
});

const interpolate = (template, vars) => {
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (_, k) =>
    vars[k] !== undefined ? String(vars[k]) : `{${k}}`
  );
};

// Look up a key in a given language. `t` from the hook is this bound to the
// active language; `translate` is the same lookup for non-React callers.
const lookup = (lang, key, varsOrFallback, maybeVars) => {
  let fallback;
  let vars;
  if (typeof varsOrFallback === 'string') {
    fallback = varsOrFallback;
    vars = maybeVars;
  } else {
    vars = varsOrFallback;
  }
  const table = dict[lang] || dict.de;
  const value = table[key];
  if (value !== undefined) return interpolate(value, vars);
  if (fallback !== undefined) return interpolate(fallback, vars);
  // Last resort: try the other language so we never show raw keys.
  const other = lang === 'de' ? dict.en[key] : dict.de[key];
  if (other !== undefined) return interpolate(other, vars);
  return key;
};

export const translate = (key, varsOrFallback, maybeVars) =>
  lookup(getLang(), key, varsOrFallback, maybeVars);

export const I18nProvider = ({ children }) => {
  const [lang, setLangState] = useState(getLang);

  useEffect(() => {
    try {
      window.localStorage.setItem(LANG_KEY, lang);
    } catch (_) {}
    if (typeof document !== 'undefined') {
      document.documentElement.setAttribute('lang', lang);
    }
  }, [lang]);

  const t = (key, varsOrFallback, maybeVars) =>
    lookup(lang, key, varsOrFallback, maybeVars);

  const setLang = (next) => {
    if (next === 'en' || next === 'de') setLangState(next);
  };

  const locale = localeFor(lang);

  return (
    <I18nContext.Provider value={{ lang, setLang, t, locale }}>
      {children}
    </I18nContext.Provider>
  );
};

export const useTranslation = () => useContext(I18nContext);

// Sugar for the very common `format(d, 'MMMM', { locale })` call.
export const useDateLocale = () => useContext(I18nContext).locale;
